/**
 * Granny Flat suggestion engine — generate & rank a curated set of ways to STRUCTURE a granny-flat
 * arrangement, optimising the real levers: avoiding unnecessary deprivation (don't pay above the assessed
 * value of the right) and how to handle the home (pay cash from savings vs transfer the home vs sell it).
 * Each candidate is evaluated with the same validated engine the manual path uses and ranked into
 * Best legacy / Best cashflow / Best Age Pension via the shared buildRecommendedOptimalStrategies.
 */
import type { DraftFactFind } from '../constants/initialFactFind.ts';
import { annuityPreferenceOf, annuityCeilingOf, allowsAnnuity, allowsResidual, allowsGuaranteed } from '../../../shared/src/calculation/annuityPreference.ts';
import { quotedIncomeFor, validQuote, type AnnuityQuote } from './annuityQuote.ts';
import { buildRecommendedOptimalStrategies, type EvaluatedStrategy, type RecommendedStrategyEntry } from './supportAtHomeRecommendedStrategies.ts';
import { candidateReadings } from './planChoiceRule.ts';
import { buildFundingVariants, type FundingMap } from './fundingMechanisms.ts';
import { annuitySizesFor } from './annuitySizes.ts';
import { EXTRA_ALLOWABLE_AMOUNT } from '../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import { grannyFlatHomeGiven, loansSecuredOnHome } from '../../../shared/src/calculation/grannyFlatArrangement.ts';
import { PENSION_RATES } from '../constants/index.ts';
import { formatCurrency } from '../utils/index.ts';
import { payableDebtsOf } from '../features/agedCare/DebtPayoffStrategy.tsx';
import { debtsWorthTesting } from './debtsToTest.ts';
import * as calculationApi from '../services/calculationApi.ts';
import { allocateNonConcessionalContribution, nonConcessionalCapacityForHousehold } from '../../../shared/src/calculation/nonConcessionalContributions.ts';
import { purchaseWithinBudget, isStampDutyState } from '../../../shared/src/calculation/stampDuty.ts';
import { suggestedPropertyFigures } from '../../../shared/src/calculation/support/investmentPropertyDefaults.ts';
import { getRecommendedEmergencyFund } from '../utils/emergencyFund.ts';
import type { AnnuityAppetite, AnnuityResidual } from '../../../shared/src/calculation/annuityPreference.ts';
import type { FactFind } from '../../../shared/src/calculation/factFindTypes.ts';

const formatAud = (n: number | undefined) => Math.round(n || 0).toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });

/*
 * THE STRATEGY ANALYSIS SETUP (owner, 2 Oct 2026) — the choices only the client can make, asked
 * once before the run, each only where a candidate would turn on it (the village's pattern):
 *   - what happens to the home: keep it (vacant, or rented out), sell it, transfer it for the
 *     right, or compare every route;
 *   - the state a bought investment property would be in (its duty scale);
 *   - whether a guaranteed income for life may be offered, and the most of the freed-up money it may
 *     take: the CLIENT's answer (annuityPreference.ts, the three answers and the ceiling, asked once,
 *     dated, read by every module; 21 Oct 2026), a quarter by default;
 *   - the emergency reserve kept in the bank before anything is paid or deployed.
 */
export type GfHomeRoute = 'keep' | 'sell' | 'transfer' | 'all';

export interface GfEngineSetup {
    homeRoute?: GfHomeRoute | null;
    /**
     * What step 1 said they would give (owner, 25 Oct 2026: "if they've told us the arrangement is going to cost
     * $600K the engine is supposed to work out the best way of achieving this … you can't just say to the builder
     * I'm going to pay you a portion of the build"). A decided form — a build with its cost, cash with its amount,
     * the home signed over — is the only form the plans use; the engine varies how it is funded, what happens to
     * the home and to any money left over. Only 'unsure' (or absent, for older callers) compares forms.
     */
    give?: 'unsure' | 'cash' | 'build' | 'home' | null;
    /** Whether the kept home is rented out, and at what weekly rent (the fact-find's likely rent by default). */
    keptHomeRent?: number | null;
    /**
     * The kept home's yearly costs when let (rates, insurance, repairs, the agent, land tax), and the weeks a
     * year it sits empty between tenants (granny flat rebuild, 21 Oct 2026: the village's ruling, two weeks
     * suggested). Absent: the profile's costs, else a quarter of the rent; two weeks empty.
     */
    keptHomeExpenses?: number | null;
    vacancyWeeks?: number | null;
    /**
     * Whether signing the home over is tested beside the other routes (step 1's "what would they give"):
     * true when they are not sure yet or would give the home; false when they would give cash or pay for a
     * build. Absent: only the 'all' and 'transfer' routes test it, as before.
     */
    withTransfer?: boolean | null;
    purchaseState?: string | null;
    /**
     * The share of the freed-up money a guaranteed income may take. Seeded from the client's ceiling
     * (annuityCeilingOf); the module's older `gfAnnuityShareCapPct` is read only as a fallback. A
     * client ceiling on the file still wins inside annuitySizesFor.
     */
    annuityShareCapPct?: number | null;
    /** The client's ceiling in dollars, when they gave one (annuityCeilingOf). */
    annuityShareCapAmount?: number | null;
    annuityAppetite?: AnnuityAppetite | null;
    annuityResidual?: AnnuityResidual | null;
    emergencyReserve?: number | null;
    /** Moving from a village (8 Oct 2026): the current unit's payout taken as paid at the move (default) or on the state's timeline. */
    oldUnitPaysAtMove?: boolean | null;
    /** The amount the client already plans to pay, where known; 0 = let the engine suggest. */
    knownAmount?: number | null;
    /**
     * What form the known amount takes (2 Oct 2026). 'cash' is tested against Centrelink's
     * reasonable value; 'build' is the cost of constructing the accommodation, which Centrelink takes
     * at face value as the price of the right — no gift, however much it costs (Social Security
     * Guide 4.6.4.50). Absent = cash.
     */
    knownAmountKind?: 'cash' | 'build' | null;
    /** Cash given on top of the build, if any — the "additional assets" the gift test then catches. */
    knownExtraCash?: number | null;
}

/** A quarter by default — "all in" kept winning the cashflow goal on the income alone (the village's lesson, 24 Sep 2026). */
export const DEFAULT_GF_ANNUITY_SHARE_CAP_PCT = 25;
/** The candidates that invest, buy or lock money away need this much freed up — the engine's own gate. */
export const GF_SETUP_SPEND_THRESHOLD = 50_000;
const GF_ANNUITY_MIN = 25_000;

export function gfAnnuityShareCapOf(setup: GfEngineSetup | null | undefined): number {
    const raw = Number(setup?.annuityShareCapPct);
    if (!Number.isFinite(raw) || setup?.annuityShareCapPct == null) return DEFAULT_GF_ANNUITY_SHARE_CAP_PCT;
    return Math.min(100, Math.max(0, raw));
}

/**
 * The state the file already holds (review, 18 Oct 2026: the setup asked for the investment
 * property's state when the downsizing setup had recorded New South Wales). In order: this module's
 * own answer, the downsizing setup's new-home state, the village setup's purchase state, then the
 * state of the village the client lives in now. '' when none is a stamp duty state.
 */
export function knownPurchaseStateOf(factFind: DraftFactFind | null | undefined): string {
    const goals = (factFind?.goals || {}) as { gfPurchaseState?: unknown; dzNewHomeState?: unknown; rvPurchaseState?: unknown };
    const village = (factFind as { retirementVillage?: { state?: unknown } } | null | undefined)?.retirementVillage;
    for (const v of [goals.gfPurchaseState, goals.dzNewHomeState, goals.rvPurchaseState, village?.state]) {
        const code = typeof v === 'string' ? v.trim().toUpperCase() : '';
        if (code && isStampDutyState(code)) return code;
    }
    return '';
}

/** Two weeks a year without a tenant, suggested (the village's ruling, carried to the granny flat 21 Oct 2026). */
export const DEFAULT_GF_VACANCY_WEEKS = 2;

/** The rent a kept home earns once the weeks it sits empty are taken off (the village's rentAfterVacancy). */
export function gfRentAfterVacancy(weeklyRent: number, vacancyWeeks: number | null | undefined): number {
    const empty = Math.min(52, Math.max(0, vacancyWeeks != null && Number.isFinite(Number(vacancyWeeks)) ? Number(vacancyWeeks) : DEFAULT_GF_VACANCY_WEEKS));
    return Math.round(((Number(weeklyRent) || 0) * (52 - empty)) / 52 * 100) / 100;
}

/** The saved setup answers, with the fact-find's likely rent and the standard reserve as the fallbacks. */
export function gfEngineSetupFromGoals(factFind: DraftFactFind | null | undefined): GfEngineSetup {
    const goals = (factFind?.goals || {}) as {
        gfHomeRoute?: string; gfPurchaseState?: string; gfAnnuityShareCapPct?: number; gfEmergencyReserve?: number;
        gfGive?: string; gfKnownAmount?: number; gfKnownExtraCash?: number; gfRentPerWeek?: number; gfRentCosts?: number; gfVacancyWeeks?: number;
    };
    const give = goals.gfGive;
    // Giving the home (step 1) is the transfer route on its own; otherwise the home's answer, with the transfer
    // tested beside it only while they are not sure what they would give.
    const savedRoute: GfHomeRoute = goals.gfHomeRoute === 'keep' || goals.gfHomeRoute === 'sell' || goals.gfHomeRoute === 'transfer' || goals.gfHomeRoute === 'all' ? goals.gfHomeRoute : 'all';
    const homeRoute: GfHomeRoute = give === 'home' ? 'transfer' : (give && savedRoute === 'transfer' ? 'all' : savedRoute);
    const withTransfer = give === 'cash' || give === 'build' ? false : give === 'unsure' ? true : null;
    const preference = annuityPreferenceOf(factFind as never);
    // The client's ceiling first; this module's older share answer only where the client has none.
    const ceiling = annuityCeilingOf(factFind as never, goals.gfAnnuityShareCapPct);
    const knownAmount = (give === 'cash' || give === 'build') && Number(goals.gfKnownAmount) > 0 ? Number(goals.gfKnownAmount) : 0;
    const rent = goals.gfRentPerWeek != null && Number(goals.gfRentPerWeek) > 0 ? Number(goals.gfRentPerWeek) : (Number(factFind?.home?.likelyRent) > 0 ? Number(factFind?.home?.likelyRent) : 0);
    return {
        homeRoute,
        give: give === 'cash' || give === 'build' || give === 'home' || give === 'unsure' ? give : null,
        withTransfer,
        keptHomeRent: rent,
        keptHomeExpenses: goals.gfRentCosts != null && Number.isFinite(Number(goals.gfRentCosts)) ? Math.max(0, Number(goals.gfRentCosts)) : null,
        vacancyWeeks: goals.gfVacancyWeeks != null && Number.isFinite(Number(goals.gfVacancyWeeks)) ? Number(goals.gfVacancyWeeks) : DEFAULT_GF_VACANCY_WEEKS,
        purchaseState: knownPurchaseStateOf(factFind),
        annuityShareCapPct: ceiling.pct ?? DEFAULT_GF_ANNUITY_SHARE_CAP_PCT,
        annuityShareCapAmount: ceiling.amount,
        annuityAppetite: preference.appetite,
        annuityResidual: preference.residual,
        emergencyReserve: Number(goals.gfEmergencyReserve) > 0 ? Number(goals.gfEmergencyReserve) : getRecommendedEmergencyFund(factFind ?? undefined),
        knownAmount,
        knownAmountKind: give === 'build' ? 'build' : 'cash',
        // Cash on top of a build, or of the home signed over (24 Oct 2026: both are face-value forms with cash tested on top).
        knownExtraCash: (give === 'build' || give === 'home') && Number(goals.gfKnownExtraCash) > 0 ? Number(goals.gfKnownExtraCash) : 0,
        oldUnitPaysAtMove: typeof (goals as { gfOldUnitPaysAtMove?: boolean }).gfOldUnitPaysAtMove === 'boolean' ? (goals as { gfOldUnitPaysAtMove?: boolean }).gfOldUnitPaysAtMove : true,
    };
}

/**
 * STEP 1 SAID "A BUILD" BUT GAVE NO COST (owner's granny flat review, 24 Oct 2026): no build can be priced, so
 * the plans pay cash instead. True so the screens can say "no build plan is tested until a cost is entered"
 * rather than leave the family wondering where the build went.
 */
export function gfBuildNeedsCost(factFind: DraftFactFind | null | undefined): boolean {
    const goals = (factFind?.goals || {}) as { gfGive?: string; gfKnownAmount?: number };
    return goals.gfGive === 'build' && !(Number(goals.gfKnownAmount) > 0);
}

/**
 * NOTHING TO SIGN OVER (24 Oct 2026): the loans on the home are as much as it is worth, so signing it over
 * gives nothing for the right and the plans do not test it. The words a screen shows, or null.
 */
export function gfTransferUnavailable(factFind: DraftFactFind | null | undefined, startDate?: string | null): string | null {
    if (!((factFind?.home?.value || 0) > 0)) return null;
    const g = grannyFlatHomeGiven(factFind as never, gfEffectiveStartDate(startDate ?? undefined) || null);
    if (g.given > 0) return null;
    return `The loans on the home (${formatAud(g.loans)}) are as much as it is worth (${formatAud(g.homeValue)}), so signing it over would give nothing for the right. It is not tested; paying the loan off first, or another way of paying, is.`;
}

/**
 * NO DATE OF BIRTH (24 Oct 2026): Centrelink's value of the right turns on age, and without a date of birth the
 * shared formula falls back to its oldest age — a tiny value, so almost anything paid in cash would read as a
 * gift. True so a screen can say the value cannot be worked out until the date of birth is entered.
 */
export function gfAgeUnknown(factFind: DraftFactFind | null | undefined): boolean {
    if (!factFind?.client?.dob) return true;
    return factFind?.adviceFor === 'couple' && !factFind?.partner?.dob;
}

/**
 * THE DATE THE RIGHT IS VALUED AND THE GIFT CLOCK STARTS (24 Oct 2026). A start date in the past models as
 * starting now - the step's own promise - so the formula value is struck at today's age and rates and the gift
 * dated today; a future date stays future. Before, a past date was used as it stood (a 2019 date gave the 2019
 * value and a gift already aged out).
 */
export function gfEffectiveStartDate(date: string | null | undefined): string {
    const today = new Date().toISOString().slice(0, 10);
    const d = typeof date === 'string' ? date.slice(0, 10) : '';
    return d && d > today ? d : today;
}

export interface GrannyFlatCandidateOptions {
    dateEntered?: string;
    /** The Strategy Analysis Setup answers (2 Oct 2026). Absent: every route, a quarter, the standard reserve. */
    setup?: GfEngineSetup;
    /**
     * A provider's quote, where the client has one: an income for ONE type and ONE amount.
     *
     * Threaded into the candidates rather than applied to the winner afterwards: the rate is what the
     * decision turns on, and a real quote can change WHICH candidate wins (owner, 2026-09-01). But a
     * quote belongs to its type (guaranteed income review M5, 21 Oct 2026; annuityQuote.ts): a plan of
     * the same type uses it scaled to its own amount, a plan of another type keeps our estimate.
     */
    annuityQuote?: AnnuityQuote | null;
    emergencyReserve?: number;
    knownAmount?: number;
    legacyYears?: number;
    /** No longer read (24 Oct 2026): kept so older callers still type-check. */
    currentInputs?: {
        transferHome?: boolean;
        propertyTransferredValue?: number;
        amountPaid?: number;
        constructionCost?: number;
        dateEntered?: string;
        fundFrom?: string;
    };
}

/** The `gf` config passed to the granny-flat calc-engine routes (buildSimulatedFactFindForGrannyFlat etc). */
export interface GrannyFlatConfig {
    /** Moving from a village (8 Oct 2026): the current unit's payout taken as paid at the move (default) or on the state's timeline. */
    oldUnitPaysAtMove?: boolean;
    amountPaid: number;
    propertyTransferredValue: number;
    constructionCost: number;
    dateEntered: string;
    fundFrom: string;
    sellingCosts?: number;
    keptHomeUse?: 'vacant' | 'rented';
    keptHomeRent?: number;
    keptHomeExpenses?: number;
    funding?: FundingMap;
    emergencyReserve?: number;
    deployment?: Record<string, unknown>;
}

export interface GrannyFlatCandidate {
    id: string;
    name: string;
    description: string;
    gf: GrannyFlatConfig;
    /**
     * Debts cleared as part of this candidate — forwarded to the engine's debt payoff plugin, the
     * same route Manual Control uses.
     *
     * The page has rendered a debt payoff card beside its inputs all along and the engine ignored
     * it, so a family could clear a mortgage by hand but was never shown it weighed against paying
     * the assessed value or keeping the money invested. Aged Care and Retirement Village both pair
     * it; this closes the gap.
     */
    debtPayoff?: { payments?: Record<string, number>; funding?: Record<string, number>; repaymentBasis?: 'keep_repayment' | 'lower_repayment' };
    // evaluateGrannyFlatCandidates (calculationApi.ts) takes Record<string, unknown>[] — this index
    // signature lets a typed GrannyFlatCandidate[] satisfy that without a cast at the call site.
    [key: string]: unknown;
}

export interface GrannyFlatSuggestionsResult {
    strategies: EvaluatedStrategy[];
    recommended: RecommendedStrategyEntry[];
    reasonableValue: number;
    /** The do-nothing benchmark, for the panel and the plan summary to measure against. */
    stayPut?: EvaluatedStrategy | null;
    /**
     * Everything a SINGLE candidate needs to be evaluated again, exactly as it was the first time.
     *
     * The deep dive's financial/calendar toggle re-cuts the candidate it is showing, and the only
     * faithful way is to hand the engine the same candidate, the same horizon and the same date
     * (owner, 23 Sep 2026). Carried whole rather than rebuilt at the call site: the evaluated row
     * does not keep everything a candidate was built with (a debt-payoff plan, for one), and the
     * date is derived inside this builder, so a second derivation there would be free to disagree.
     */
    rerun: { candidates: unknown[]; legacyYears: number; date?: string };
}

/** Probe the engine for the assessed reasonable value of the right at this person's age (today). */
export async function getGrannyFlatReasonableValueForClient(factFind: DraftFactFind, dateEntered?: string): Promise<number> {
    const date = dateEntered || new Date().toISOString().slice(0, 10);
    const probe = await calculationApi.buildSimulatedFactFindForGrannyFlat(factFind, { amountPaid: 1, dateEntered: date });
    const dep = await calculationApi.getGrannyFlatDeprivationFromFactFind(probe, PENSION_RATES.couple);
    return dep?.reasonableValue || 0;
}

/**
 * Curated candidate structures (config only). Step 1's own amount (a build, or cash) is tested in every home
 * route (gf-known*); `currentInputs` is no longer read (24 Oct 2026).
 */
export async function generateGrannyFlatCandidates(factFind: DraftFactFind, options: GrannyFlatCandidateOptions = {}): Promise<GrannyFlatCandidate[]> {
    const dateEntered = gfEffectiveStartDate(options.dateEntered);
    const hasHome = (factFind?.home?.value || 0) > 0;
    const homeValue = factFind?.home?.value || 0;
    const reasonableValueRaw = await getGrannyFlatReasonableValueForClient(factFind, dateEntered);
    // Pay the FLOORED reasonable value so we never tip a dollar over it (which would create a tiny gift).
    const fairAmount = Math.floor(reasonableValueRaw);
    const reasonableValueDisplay = Math.round(reasonableValueRaw);
    const setup: GfEngineSetup = options.setup ?? {};
    // Emergency funds to keep in the bank rather than spend on the granny-flat payment (from the setup).
    const reserve = Math.max(0, Number(setup.emergencyReserve ?? options.emergencyReserve) || 0);
    /*
     * WHAT HAPPENS TO THE HOME — the client's choice, from the setup. 'all' (the default) models
     * every route; a chosen route narrows the field to it. The do-not-proceed benchmark, the
     * move-in-only candidate and the client's own plan are route-independent.
     */
    const homeRoute: GfHomeRoute = setup.homeRoute === 'keep' || setup.homeRoute === 'sell' || setup.homeRoute === 'transfer' ? setup.homeRoute : 'all';
    const routeKeep = homeRoute === 'all' || homeRoute === 'keep' || !hasHome;
    const routeSell = hasHome && (homeRoute === 'all' || homeRoute === 'sell');
    // Signing the home over: its own route, or beside the others while the client is not sure what they would give.
    // What signing the home over gives: its value on the start date, less the loans on it, which the family
    // member takes over (24 Oct 2026). Nothing left once the loans are paid → nothing to sign over.
    const homeGiven = grannyFlatHomeGiven(factFind as never, dateEntered || null);
    const transferValue = Math.round(homeGiven.given);
    const loanWords = homeGiven.loans >= 1 ? ` (the ${formatAud(homeGiven.homeValue)} home${homeGiven.grown ? ' at the start date' : ''}, less the ${formatAud(homeGiven.loans)} loan the family takes over with it)` : homeGiven.grown ? ' (its value at the start date)' : '';
    const routeTransfer = hasHome && transferValue > 0 && (homeRoute === 'transfer' || (setup.withTransfer != null ? setup.withTransfer === true : homeRoute === 'all'));
    /*
     * THE CLIENT'S ANSWER (guaranteed income review, 21 Oct 2026; annuityPreference.ts). "No" builds no
     * guaranteed income at all; "Yes, if the money goes back" ('back', and the unanswered default) tests
     * money back, and the full amount back where the person may buy it; "Yes, and the most income"
     * ('all') adds income only. The setup's answer wins where it carries one (it is seeded from the
     * client record and written back to it); every value, the older 'returned' / 'nil' / 'guaranteed' /
     * 'both' included, is read the way annuityPreferenceOf reads it, so nothing here narrows it again.
     */
    const goalsNow = (factFind?.goals || {}) as Record<string, unknown>;
    const gfAnnuityPreference = annuityPreferenceOf({
        ...factFind,
        goals: {
            ...goalsNow,
            ...(setup.annuityAppetite ? { annuityAppetite: setup.annuityAppetite } : {}),
            ...(setup.annuityResidual ? { annuityResidual: setup.annuityResidual } : {}),
        },
    } as never);
    const annuityCapPct = allowsAnnuity(gfAnnuityPreference) ? gfAnnuityShareCapOf(setup) : 0;
    // The client's ceiling in dollars, where the setup carries it and the file does not hold an answer yet.
    const sizesFactFind = Number(setup.annuityShareCapAmount) > 0 && !(Number(goalsNow.annuityShareCapAmount) > 0) && !(Number(goalsNow.annuityShareCapPct) > 0)
        ? ({ ...factFind, goals: { ...goalsNow, annuityShareCapAmount: Number(setup.annuityShareCapAmount) } } as DraftFactFind)
        : factFind;
    const quote = validQuote(options.annuityQuote);
    /** The provider's quote for this plan's type and amount, scaled; undefined keeps our estimate. */
    const quotedFor = (annuityType: string, residualPercent: number, amount: number) => quotedIncomeFor(quote, annuityType, residualPercent, amount);
    /** The property a budget buys once its stamp duty is paid, with the rent struck on the PRICE (the village's rule). */
    const purchaseState = isStampDutyState(setup.purchaseState) ? setup.purchaseState : undefined;
    const propertyFor = (budget: number) => {
        // Investor rates; the firm's yield, two weeks empty, 8% agent and 1% upkeep (investmentPropertyDefaults.ts, 21 Oct 2026).
        const bought = purchaseWithinBudget(purchaseState, budget, 'investment');
        const f = suggestedPropertyFigures(bought.price);
        return { investmentProperty: bought.price, ipStampDuty: bought.duty, ipRent: f.rent, ipExpenses: f.expenses };
    };
    const dutyWords = (duty: number) => (purchaseState
        ? ` Stamp duty of ${formatAud(duty)} (${purchaseState}) comes out of the proceeds, so the property is sized to what is left.`
        : ' Stamp duty on the property is not modelled — no state was chosen.');

    const candidates: GrannyFlatCandidate[] = [];

    /*
     * 0. DO NOT PROCEED — the benchmark, and the floor every arrangement must beat. Excluded from the
     *    ranked universe and passed as the benchmark instead (see buildGrannyFlatSuggestions).
     */
    candidates.push({
        id: 'gf-stay-put',
        name: 'Do not enter the arrangement',
        description: 'No granny flat arrangement — life as it is today. This is the baseline every option below is measured against.',
        gf: { oldUnitPaysAtMove: setup.oldUnitPaysAtMove !== false, amountPaid: 0, propertyTransferredValue: 0, constructionCost: 0, dateEntered: '', fundFrom: 'liquid' },
        stayPut: true,
    });

    // 1. Pay the assessed (reasonable) value from savings — no gift. The default "clean" structure (home kept,
    //    vacant). The SE COMPARES funding mechanisms: when the payment needs more than cash and the client holds
    //    both shares and an account-based pension, we generate BOTH (draw the ABP — no CGT — vs sell shares — CGT),
    //    and the projection's after-CGT / after-return outcome decides which wins.
    /*
     * THE FORM THE FAMILY CHOSE, IN EVERY HOME ROUTE (owner's granny flat review, 24 Oct 2026: a build was chosen
     * with the home kept and rented, and no build-and-rent plan was ever tested - every rented and sold plan paid
     * cash at the formula value). A build (with its cost) or a known cash amount is tested with the home kept
     * empty, kept and let, and sold — and, since 25 Oct 2026, ONLY that form (see "A DECIDED FORM IS THE PLAN").
     */
    const knownAmount = Math.round(Number(setup.knownAmount) > 0 ? Number(setup.knownAmount) : (options.knownAmount || 0));
    const knownIsBuild = setup.knownAmountKind === 'build';
    const knownExtraCashAll = Math.max(0, Math.round(Number(setup.knownExtraCash) || 0));
    /** What the chosen form gives: the build (face value) with any cash on top, or the cash amount. */
    const knownGf = knownIsBuild
        ? { amountPaid: knownExtraCashAll, propertyTransferredValue: 0, constructionCost: knownAmount }
        : { amountPaid: knownAmount, propertyTransferredValue: 0, constructionCost: 0 };
    const knownTotal = knownAmount + (knownIsBuild ? knownExtraCashAll : 0);
    const knownWords = knownIsBuild
        ? `Pay ${formatAud(knownAmount)} for the build${knownExtraCashAll > 0 ? ` plus ${formatAud(knownExtraCashAll)} cash` : ''}`
        : `Pay the ${formatAud(knownAmount)} planned, in cash`;
    const knownLower = knownWords.charAt(0).toLowerCase() + knownWords.slice(1);
    const knownGiftWords = (() => {
        const interest = knownIsBuild ? Math.max(fairAmount, knownAmount) : fairAmount;
        const gift = Math.max(0, knownTotal - interest);
        if (knownIsBuild && knownExtraCashAll <= 0) return ' Centrelink takes the cost of the build as the price of the right, so none of it is a gift.';
        return gift > 0 ? ` About ${formatAud(gift)} of it is above what the right is worth, so it is treated as a gift and counted for five years.` : ' None of it is a gift.';
    })();

    /*
     * A DECIDED FORM IS THE PLAN (owner, 25 Oct 2026: "if they've told us the arrangement is going to cost $600K the
     * engine is supposed to work out the best way of achieving this … you can't just say to the builder I'm going to
     * pay you a portion of the build"). With a build and its cost, cash and its amount, or the home signed over, every
     * plan pays exactly that; what the engine varies is where the money comes from, what happens to the home, and
     * what is done with anything left from a sale. Only "not sure yet" compares the forms themselves: cash at
     * Centrelink's limit, the home signed over, and moving in with nothing paid. (Until now the plans that paid cash
     * at the limit, or nothing, stood beside a decided build, marked "not what was described".)
     */
    const give = setup.give ?? (knownAmount > 0 ? (knownIsBuild ? 'build' : 'cash') : null);
    const decidedKnown = (give === 'build' || give === 'cash') && knownAmount > 0;
    const decidedHome = give === 'home';
    const buildNoCost = give === 'build' && !(knownAmount > 0);
    // Cash chosen with no amount: Centrelink's limit is the cash plan, so the limit plans ARE the chosen form.
    const decidedCashAtLimit = give === 'cash' && !(knownAmount > 0);
    const comparingForms = !decidedKnown && !decidedHome && !buildNoCost && !decidedCashAtLimit;

    // A build without its cost cannot be priced: no plan, and the screens say so (gfBuildNeedsCost).
    if (buildNoCost) return candidates;

    // 1. The home kept, empty: the payment from savings, one plan per way of funding it.
    if (routeKeep && !decidedKnown && !decidedHome) {
        buildFundingVariants(factFind, fairAmount, reserve).forEach((v) => {
            candidates.push({
                id: `gf-fair-cash${v.key}`,
                name: `${hasHome ? 'Pay the assessed value, keep your home vacant' : 'Pay the assessed value (no gift)'}${v.nameSuffix}`,
                description: `Pay about ${formatAud(reasonableValueDisplay)} — the value Centrelink puts on your lifetime right at your age — from your savings. Nothing counts as a gift.${v.note ? ' ' + v.note : ''}${hasHome ? ' Your former home is kept but left vacant — it becomes an assessable asset.' : ''}`,
                gf: { oldUnitPaysAtMove: setup.oldUnitPaysAtMove !== false, amountPaid: fairAmount, propertyTransferredValue: 0, constructionCost: 0, dateEntered, fundFrom: 'savings', keptHomeUse: 'vacant', funding: Object.keys(v.funding).length ? v.funding : undefined, emergencyReserve: reserve },
            });
        });
    }
    if (routeKeep && decidedKnown && knownIsBuild) {
        /*
         * PAYING FOR THE BUILD (2 Oct 2026). The cost of constructing the accommodation is one of the
         * three forms Centrelink accepts at face value (home title, construction, a property bought in
         * the family's name): no reasonableness test, no gift, however much it costs. Cash on top is
         * "additional assets" — then the whole amount is tested against the greater of the build cost
         * and the formula (shared/src/calculation/grannyFlatArrangement.ts). Both money out is funded
         * from the client's assets, so the funding variants target the total.
         */
        const extraCash = knownExtraCashAll;
        const total = knownAmount + extraCash;
        const interest = Math.max(fairAmount, knownAmount);
        const gift = Math.max(0, total - interest);
        buildFundingVariants(factFind, total, reserve).forEach((v) => {
            candidates.push({
                id: `gf-known${v.key}`,
                name: `Pay for the build (${formatAud(knownAmount)})${extraCash > 0 ? ` plus ${formatAud(extraCash)} cash` : ''}${v.nameSuffix}`,
                description: `Pay ${formatAud(knownAmount)} to build or extend the family's home for your accommodation, from your savings. Centrelink takes the cost of the build as the price of the right, so on its own none of it is a gift${knownAmount > fairAmount ? ` — even though it is above the formula value of ${formatAud(reasonableValueDisplay)}` : ''}.${extraCash > 0 ? ` The ${formatAud(extraCash)} given on top is tested: the right is valued at ${formatAud(interest)}, so ${gift > 0 ? `about ${formatAud(gift)} is treated as a gift and counted for 5 years` : 'nothing counts as a gift'}.` : ''}${v.note ? ' ' + v.note : ''}`,
                gf: { oldUnitPaysAtMove: setup.oldUnitPaysAtMove !== false, amountPaid: extraCash, propertyTransferredValue: 0, constructionCost: knownAmount, dateEntered, fundFrom: 'savings', keptHomeUse: 'vacant', funding: Object.keys(v.funding).length ? v.funding : undefined, emergencyReserve: reserve },
            });
        });
    } else if (routeKeep && decidedKnown) {
        const overAssessed = knownAmount > fairAmount;
        buildFundingVariants(factFind, knownAmount, reserve).forEach((v) => {
            candidates.push({
                id: `gf-known${v.key}`,
                name: `Pay your planned amount (${formatAud(knownAmount)})${v.nameSuffix}`,
                description: `Pay the ${formatAud(knownAmount)} you plan to give in cash, from your savings.${overAssessed ? ` This is above Centrelink's reasonable value (${formatAud(reasonableValueDisplay)}), so about ${formatAud(knownAmount - fairAmount)} is treated as a gift and counted for 5 years.` : ` This is at or below the reasonable value (${formatAud(reasonableValueDisplay)}), so nothing counts as a gift.`}${v.note ? ' ' + v.note : ''}`,
                gf: { oldUnitPaysAtMove: setup.oldUnitPaysAtMove !== false, amountPaid: knownAmount, propertyTransferredValue: 0, constructionCost: 0, dateEntered, fundFrom: 'savings', keptHomeUse: 'vacant', funding: Object.keys(v.funding).length ? v.funding : undefined, emergencyReserve: reserve },
            });
        });
    }

    if (hasHome && routeKeep && !decidedHome) {
        // 1b. Same, but RENT OUT the kept former home. Once you move into the granny flat the former home is an
        // assessable asset whether or not it's rented — so renting it is usually pure extra cashflow/estate (the
        // pension is already affected by the asset). Uses the setup's rent, else the client's likely rent (or ~3.5% gross yield).
        const grossWeeklyRent = Math.round(Number(setup.keptHomeRent) > 0 ? Number(setup.keptHomeRent) : (factFind?.home?.likelyRent || (homeValue * 0.035) / 52));
        /*
         * THE WEEKS IT SITS EMPTY (granny flat rebuild, 21 Oct 2026 — the village's ruling): between tenants
         * the rent stops and the costs go on, so the rent modelled is the gross rent over the weeks it is let
         * (two empty weeks a year, suggested). The costs are the client's, itemised on the questions screen
         * (rates, insurance, repairs, the agent, land tax); absent, the profile's, else a quarter of the rent.
         */
        const weeklyRent = gfRentAfterVacancy(grossWeeklyRent, setup.vacancyWeeks);
        const annualExpenses = Math.round(setup.keptHomeExpenses != null && Number.isFinite(Number(setup.keptHomeExpenses))
            ? Number(setup.keptHomeExpenses)
            : (factFind?.home?.likelyExpenses || (grossWeeklyRent * 52 * 0.25)));
        if (!decidedKnown) {
            candidates.push({
                id: 'gf-keep-rent',
                name: 'Pay the assessed value and rent out your home',
                description: `Pay the assessed value from savings (no gift), keep your former home and rent it out for about ${formatAud(weeklyRent * 52)}/yr after the weeks it sits empty. The home is assessable either way, so the rent is extra income. Heads-up: the main-residence CGT exemption lasts up to 6 years after you move out — after that, capital gains tax can apply when you sell (you can only have one main residence at a time).`,
                gf: { oldUnitPaysAtMove: setup.oldUnitPaysAtMove !== false, amountPaid: fairAmount, propertyTransferredValue: 0, constructionCost: 0, dateEntered, fundFrom: 'savings', keptHomeUse: 'rented', keptHomeRent: weeklyRent, keptHomeExpenses: annualExpenses, emergencyReserve: reserve },
            });
        } else {
            // The decided form with the home let, one plan per way of funding it.
            buildFundingVariants(factFind, knownTotal, reserve).forEach((v) => {
                candidates.push({
                    id: `gf-known-keep-rent${v.key}`,
                    name: `${knownWords}, and rent out your home${v.nameSuffix}`,
                    description: `${knownWords} from your savings, and keep your former home and rent it out for about ${formatAud(weeklyRent * 52)}/yr after the weeks it sits empty.${knownGiftWords} The home is counted either way, so the rent is extra income.${v.note ? ' ' + v.note : ''}`,
                    gf: { oldUnitPaysAtMove: setup.oldUnitPaysAtMove !== false, ...knownGf, dateEntered, fundFrom: 'savings', keptHomeUse: 'rented', keptHomeRent: weeklyRent, keptHomeExpenses: annualExpenses, funding: Object.keys(v.funding).length ? v.funding : undefined, emergencyReserve: reserve },
                });
            });
        }
    }

    // 2. The home signed over: decided, or one of the forms compared while they are not sure.
    if (routeTransfer && (decidedHome || comparingForms)) {
        if (knownExtraCashAll > 0 && !knownIsBuild) {
            // The home signed over WITH cash on top (24 Oct 2026): the cash is tested - the right is valued at the
            // greater of the home and the formula, and anything given above that is a gift. One plan per way of
            // funding the cash.
            const interest = Math.max(fairAmount, transferValue);
            const gift = Math.max(0, transferValue + knownExtraCashAll - interest);
            buildFundingVariants(factFind, knownExtraCashAll, reserve).forEach((v) => {
                candidates.push({
                    id: `gf-transfer-home-cash${v.key}`,
                    name: `Transfer your home for the right, plus ${formatAud(knownExtraCashAll)} cash${v.nameSuffix}`,
                    description: `Sign your home over to the family${loanWords} and add ${formatAud(knownExtraCashAll)} in cash. The home on its own is taken at face value; the cash on top is tested - ${gift > 0 ? `about ${formatAud(gift)} is above what the right is worth, so it is treated as a gift and counted for five years` : 'none of it is a gift'}.${v.note ? ' ' + v.note : ''}`,
                    gf: { oldUnitPaysAtMove: setup.oldUnitPaysAtMove !== false, amountPaid: knownExtraCashAll, propertyTransferredValue: transferValue, constructionCost: 0, dateEntered, fundFrom: 'savings', funding: Object.keys(v.funding).length ? v.funding : undefined, emergencyReserve: reserve },
                });
            });
        }
        // The title on its own: taken at face value as the price of the right (Social Security Guide 4.6.4.50),
        // whatever the home is worth. Not a plan when the decided form is the home WITH cash on top.
        if (!(decidedHome && knownExtraCashAll > 0)) {
            candidates.push({
                id: 'gf-transfer-home',
                name: 'Transfer your home for the right',
                description: `Sign your home over to the family in return for the lifetime right to live there${loanWords}. Centrelink takes what is given as the price of the right, so none of it is a gift — however much the home is worth${transferValue > EXTRA_ALLOWABLE_AMOUNT ? ' — and you stay a homeowner for the pension' : `. At ${formatAud(transferValue)} it is not more than ${formatAud(EXTRA_ALLOWABLE_AMOUNT)}, so you are not a homeowner for the pension and the right is counted as an asset`}.`,
                gf: { oldUnitPaysAtMove: setup.oldUnitPaysAtMove !== false, amountPaid: 0, propertyTransferredValue: transferValue, constructionCost: 0, dateEntered, fundFrom: 'savings' },
            });
        }
    }

    // 3. Sell the home: the payment from the proceeds, and the rest kept, invested, or put to work.
    if (routeSell && !decidedHome) {
        const sellCosts = Math.round(homeValue * 0.025);
        // Loans on the home are repaid from the sale (24 Oct 2026), so they are not there to invest.
        const homeLoansRepaid = loansSecuredOnHome(factFind as never);
        /*
         * THE EMERGENCY RESERVE applies to the sale route too (owner, 2 Oct 2026): what the bank
         * already holds counts towards it, and only the shortfall is held back from the proceeds.
         */
        const bankToday = (factFind.assets?.bankAccounts || []).reduce((sum, a) => sum + (Number(a?.value) || 0), 0);
        const reserveTopUp = Math.max(0, reserve - bankToday);
        const reserveWords = reserveTopUp > 0 ? ` ${formatAud(reserveTopUp)} of the proceeds stays in the bank as the emergency reserve.` : '';
        /*
         * WHAT IS PAID FROM THE SALE: the decided form exactly (a build, a cash amount), else cash at Centrelink's
         * limit. The same family of plans either way — the rest in the bank, invested, in property, in a guaranteed
         * income, in super or a bond — so the decided form is tested as thoroughly as the limit ever was.
         */
        const payTotal = decidedKnown ? knownTotal : fairAmount;
        const pay = decidedKnown ? `${knownLower} from the proceeds` : 'pay the assessed value for your right (no gift)';
        const payGift = decidedKnown ? knownGiftWords : '';
        const idOf = (fairId: string, knownId: string) => (decidedKnown ? knownId : fairId);
        const nameOf = (fairName: string, knownTail: string) => (decidedKnown ? `Sell your home; ${knownLower}${knownTail}` : fairName);
        const residualAfterPay = Math.max(0, homeValue - sellCosts - homeLoansRepaid - payTotal - reserveTopUp);
        // Pin sellingCosts so the deployed amount equals the scenario's residual exactly (no rounding over-deploy).
        const baseSell = decidedKnown
            ? { oldUnitPaysAtMove: setup.oldUnitPaysAtMove !== false, ...knownGf, dateEntered, fundFrom: 'sell_home', sellingCosts: sellCosts }
            : { amountPaid: fairAmount, propertyTransferredValue: 0, constructionCost: 0, dateEntered, fundFrom: 'sell_home', sellingCosts: sellCosts };

        candidates.push(decidedKnown
            ? {
                id: 'gf-known-sell',
                name: `Sell your home; ${knownLower} from the sale`,
                description: `Sell the home and ${knownLower} from the proceeds; keep the rest in the bank.${knownGiftWords}`,
                gf: { ...baseSell },
            }
            : {
                id: 'gf-sell-pay-fair',
                name: 'Sell your home and pay the assessed value',
                description: 'Sell the home, pay the assessed value for your right (no gift), and keep the rest in the bank — more cash on hand, but more is counted for the pension.',
                gf: { oldUnitPaysAtMove: setup.oldUnitPaysAtMove !== false, amountPaid: fairAmount, propertyTransferredValue: 0, constructionCost: 0, dateEntered, fundFrom: 'sell_home' },
            });

        if (residualAfterPay >= GF_SETUP_SPEND_THRESHOLD) {
            candidates.push({
                id: idOf('gf-sell-invest', 'gf-known-sell-invest'),
                name: nameOf('Sell your home and invest the rest', ' and invest the rest'),
                description: `Sell the home, ${pay}, and put the freed-up money into a diversified share / managed-fund portfolio instead of the bank — aiming for higher long-term growth and income (still counted for the pension; returns aren't guaranteed).${payGift}${reserveWords}`,
                gf: { ...baseSell, deployment: { shares: residualAfterPay } },
            });

            const ip = propertyFor(residualAfterPay);
            if (ip.investmentProperty > 0) candidates.push({
                id: idOf('gf-sell-property', 'gf-known-sell-property'),
                name: nameOf('Sell your home and buy an investment property', ' and buy an investment property'),
                description: `Sell the home, ${pay}, and use the freed-up money to buy an investment property for rental income and potential growth. The property is an assessable asset and the net rent counts for the pension.${dutyWords(ip.ipStampDuty)}${payGift}${reserveWords}`,
                gf: { ...baseSell, deployment: ip },
            });

            /*
             * BOTH ENDS OF THE RESIDUAL-CAPITAL TRADE-OFF, or the strategy is disqualified rather
             * than weighed.
             *
             * Centrelink assesses a lifetime annuity at 60% of its purchase price WHATEVER its
             * residual capital value — RCV changes the payment rate, not the assets-test treatment.
             * But net worth values an annuity at its RESIDUAL (assessment/netWorth.ts, deliberately:
             * a nil-RCV lifetime annuity really does leave nothing behind), so a nil-RCV candidate
             * loses the legacy ranking by its full purchase price on day one. It was never being
             * compared against the alternatives; it was being ruled out before the comparison began.
             *
             * The Downsizer, Retirement Village, Support at Home and Aged Care engines were all fixed
             * for this. This module and Cashflow & Age Pension were built afterwards and inherited
             * the original single-variant shape — which is exactly why the guard now covers every
             * module in annuityResidualCapitalVariants.test.ts rather than the four it started with.
             */
            const annuityMeansTest = 'only 60% of the purchase price counted for the assets test (for the first five years, or until age 85 if that is later, and 30% after that), which often lifts the Age Pension';
            // NEVER all of it: the setup's share goes into the annuity, the rest into shares — and, where it
            // lifts the Age Pension, the amount that lifts it most as a plan of its own (annuitySizes.ts,
            // 21 Oct 2026). A granny flat right counts as a home when more than the extra allowable amount was paid for it.
            annuitySizesFor(sizesFactFind, { investable: residualAfterPay, capPct: annuityCapPct, homeowner: payTotal > EXTRA_ALLOWABLE_AMOUNT }).forEach((size) => {
                const annuityShare = size.amount;
                const tail = size.pensionMax ? '-max' : '';
                const sizeName = size.pensionMax ? ', the amount that lifts the Age Pension most' : '';
                const annuityRest = Math.max(0, residualAfterPay - annuityShare);
                const restWords = annuityRest > 0 ? ` The remaining ${formatAud(annuityRest)} goes into shares.` : '';
                const annuityId = (kind: string) => idOf(`gf-sell-annuity${tail}${kind}`, `gf-known-sell-annuity${tail}${kind}`);
                const annuityName = (shape: string) => nameOf(`Sell your home and buy a guaranteed income for life (${shape}${sizeName})`, ` and buy a guaranteed income for life (${shape}${sizeName})`);
                if (annuityShare >= GF_ANNUITY_MIN && allowsResidual(gfAnnuityPreference, 0)) candidates.push({
                    id: annuityId(''),
                    name: annuityName('income only'),
                    description: `Sell the home, ${pay}, and put ${formatAud(annuityShare)} of the freed-up money into a guaranteed income for life, with ${annuityMeansTest}. This type pays the most income, and nothing comes back to the family, even after an early death. Tested because it was asked for.${restWords}${payGift}${reserveWords}`,
                    gf: { ...baseSell, deployment: { annuity: annuityShare, annuityType: 'lifetime', annuityResidualPercent: 0, annuityIncomeOverride: quotedFor('lifetime', 0, annuityShare), ...(annuityRest > 0 ? { shares: annuityRest } : {}) } },
                });
                if (annuityShare >= GF_ANNUITY_MIN && allowsResidual(gfAnnuityPreference, 1)) candidates.push({
                    id: annuityId('-rcv'),
                    name: annuityName('money back'),
                    description: `Sell the home, ${pay}, and put ${formatAud(annuityShare)} of the freed-up money into a guaranteed income for life, with ${annuityMeansTest}. The money goes back to the family on an early death: the whole amount in about the first half of life expectancy, then a smaller amount, falling to nothing.${restWords}${payGift}${reserveWords}`,
                    gf: { ...baseSell, deployment: { annuity: annuityShare, annuityType: 'lifetime', annuityResidualPercent: 100, annuityIncomeOverride: quotedFor('lifetime', 100, annuityShare), ...(annuityRest > 0 ? { shares: annuityRest } : {}) } },
                });
                if (annuityShare >= GF_ANNUITY_MIN && allowsGuaranteed(gfAnnuityPreference, factFind as never)) candidates.push({
                    id: annuityId('-guaranteed'),
                    name: annuityName('full amount back'),
                    description: `Sell the home, ${pay}, and put ${formatAud(annuityShare)} of the freed-up money into a guaranteed income for life combined with life cover, so the full amount goes back to the family whenever death comes. It pays the least income of the types, and only the income part earns the 60% assets-test treatment; the life cover is counted at what it cost.${restWords}${payGift}${reserveWords}`,
                    gf: { ...baseSell, deployment: { annuity: annuityShare, annuityType: 'guaranteed', annuityResidualPercent: 100, annuityIncomeOverride: quotedFor('guaranteed', 100, annuityShare), ...(annuityRest > 0 ? { shares: annuityRest } : {}) } },
                });
            });

            /*
             * 7-8. AFTER-TAX SUPER (F.6) AND AN INVESTMENT BOND (F.5) for the residual — 17 Sep 2026.
             * Owner-directed: a granny flat funded by selling the home leaves residual money too, and
             * it deserves the same homes the Downsizer offers. Logic-based: super only where the
             * person may still contribute (under 75, room under the balance bands), the bond as the
             * tax-paid alternative where they may not, or for what the caps cannot take.
             */
            const ff = factFind as unknown as FactFind;
            const superRoom = nonConcessionalCapacityForHousehold(ff);
            const bondBlurb = 'An investment bond pays its own tax inside (30%) and nothing on your tax return; after ten years withdrawals are tax-free. Centrelink counts it in full and deems it, like shares, so this is about tax, not the pension.';
            if (superRoom.total > 0) {
                const ncc = allocateNonConcessionalContribution(ff, residualAfterPay);
                const nccTotal = ncc.client + ncc.partner;
                if (nccTotal >= 25_000) {
                    candidates.push({
                        id: idOf('gf-sell-super', 'gf-known-sell-super'),
                        name: nameOf('Sell your home, pay the assessed value, and top up super after tax', ', and top up super after tax'),
                        description: `Sell the home, ${pay}, and put ${formatAud(nccTotal)} of the rest into super as an after-tax contribution${ncc.preferred ? ` — ${ncc.preferred === 'client' ? (factFind?.client?.firstName || 'the client') : (factFind?.partner?.firstName || 'the partner')} first, whose accumulation super is not counted by Centrelink until Age Pension age` : ''}. Capped at what each of you may contribute now; anything above the cap goes into shares.${payGift}`,
                        gf: { ...baseSell, deployment: { superContribution: ncc.client, partnerSuperContribution: ncc.partner, superCommenceIncomeStream: ncc.preferred == null, shares: Math.max(0, residualAfterPay - nccTotal) } },
                    });
                }
                if (residualAfterPay - superRoom.total >= GF_SETUP_SPEND_THRESHOLD) {
                    candidates.push({
                        id: idOf('gf-sell-super-and-bond', 'gf-known-sell-super-and-bond'),
                        name: nameOf('Sell your home, fill super after tax, and bond the rest', ', fill super after tax, and bond the rest'),
                        description: `${decidedKnown ? `Sell the home, ${pay}. ` : ''}Put ${formatAud(nccTotal)} into super as an after-tax contribution — the most the caps allow — and the remaining ${formatAud(residualAfterPay - nccTotal)} into an investment bond. ${bondBlurb}${payGift}`,
                        gf: { ...baseSell, deployment: { superContribution: ncc.client, partnerSuperContribution: ncc.partner, superCommenceIncomeStream: ncc.preferred == null, investmentBond: Math.max(0, residualAfterPay - nccTotal) } },
                    });
                }
            } else {
                candidates.push({
                    id: idOf('gf-sell-bond', 'gf-known-sell-bond'),
                    name: nameOf('Sell your home, pay the assessed value, and bond the rest', ', and bond the rest'),
                    description: `Sell the home, ${pay}, and put the remaining ${formatAud(residualAfterPay)} into an investment bond — the tax-paid home for money super can no longer take. ${bondBlurb}${payGift}`,
                    gf: { ...baseSell, deployment: { investmentBond: residualAfterPay } },
                });
            }
        }
    }

    /*
     * "YOUR CURRENT PLAN" (gf-your-plan) WAS REMOVED 24 Oct 2026: no screen ever passed the hand-built plan in, so it
     * was never generated; the plans built from step 1's own amount (gf-known*) are that comparison now.
     */

    /*
     * MOVE IN AND PAY NOTHING — the option every family raises first, and the engine never modelled.
     *
     * This is NOT the same thing as the do-nothing benchmark, which is not moving at all. Here the
     * client moves in with family without paying for the right to live there: no granny-flat
     * interest is created, so nothing counts as a gift and there is no reasonableness test to
     * satisfy. It looks like the cheapest option on the page, and it usually is in cash terms.
     *
     * What it does NOT avoid is the consequence families are most surprised by. Centrelink exempts
     * ONE home, and only while you live in it. Move out and the house you still own becomes an
     * ordinary counted asset — on the case this was written against, that alone takes a $29,824
     * pension to nil and turns a $9,176 cashflow gap into $39,000. Paying nothing does not protect
     * the pension; living somewhere else is what costs it.
     *
     * So this is offered as a candidate precisely so the ranking can show that. An engine that only
     * ever priced arrangements with a payment left the family to discover the free option's cost on
     * their own. One of the forms compared while they are not sure; not a plan once a form is decided.
     */
    if (comparingForms) candidates.push({
        id: 'gf-move-in-only',
        name: 'Move in without paying for the right',
        description:
            'Move in with family and pay nothing for the right to live there. No granny-flat interest is created, so nothing counts as a gift and there is no assessed value to meet — but the home you leave stops being exempt the moment you stop living in it, and is counted like any other asset from then on. Rent Assistance generally does not apply when living with family.',
        gf: {
            amountPaid: 0,
            propertyTransferredValue: 0,
            constructionCost: 0,
            dateEntered,
            fundFrom: 'liquid',
        },
    });

    /*
     * DEBT CLEARANCE — paired against every arrangement above, the same pattern the aged care and
     * village engines use. The interest stops and the repayments leave the cashflow, but the money
     * spent is money that cannot also pay the family for the right to live there — which for a
     * household doing both at once is exactly the competition worth ranking.
     *
     * The do-nothing benchmark is left pure: it has to stay indistinguishable from the client's life
     * as it is, or nothing else has anything to be measured against.
     *
     * Which debts: strategy/debtsToTest.ts (never a card paid off monthly; any interest-bearing debt;
     * interest-free debts from $5,000), the same rule every module uses.
     */
    const debts = debtsWorthTesting(payableDebtsOf(factFind));
    const totalDebt = debts.reduce((sum, d) => sum + d.balance, 0);
    if (debts.length > 0) {
        const payments = Object.fromEntries(debts.map((d) => [d.key, d.balance]));
        const twins = candidates
            .filter((c) => c.stayPut !== true)
            .map((c) => ({
                ...c,
                id: `${c.id}-clear-debt`,
                name: `${c.name}, and clear your debts`,
                description: `${c.description} This version also clears your outstanding debts of ${formatCurrency(totalDebt)} at the same time — the interest stops and the repayments leave your cashflow, but that money is no longer available for anything else in the plan.`,
                debtPayoff: { payments, repaymentBasis: 'keep_repayment' as const },
            }));
        candidates.push(...twins);
    }

    return candidates;
}

/**
 * Evaluate every candidate with the engine and rank into goal leaders.
 */
export async function buildGrannyFlatSuggestions(factFind: DraftFactFind, options: GrannyFlatCandidateOptions = {}): Promise<GrannyFlatSuggestionsResult> {
    const legacyYears = options.legacyYears || 10;
    const dateEntered = gfEffectiveStartDate(options.dateEntered);
    const reasonableValue = Math.round(await getGrannyFlatReasonableValueForClient(factFind, dateEntered));
    const candidates = await generateGrannyFlatCandidates(factFind, { ...options, dateEntered });

    // One request evaluates every candidate against the engine server-side (see
    // server/src/rest/calculation/suggestionEngine/grannyFlat.ts) instead of 3 calls x N candidates.
    // A FUTURE entry date defers every candidate to the same day as the manual run — timing belongs
    // to the case. Today or the past means the arrangement starts now, the long-standing behaviour.
    const today = new Date().toISOString().slice(0, 10);
    const futureEntryDate = options.dateEntered && options.dateEntered > today ? options.dateEntered : undefined;
    const strategies: EvaluatedStrategy[] = await calculationApi.evaluateGrannyFlatCandidates(factFind, candidates, legacyYears, futureEntryDate);

    // Ranked among the real arrangements, each measured against not proceeding — so nothing is
    // recommended that leaves the client worse off than doing nothing.
    const stayPut = strategies.find((s) => s.id === 'gf-stay-put') || null;
    const arrangements = strategies.filter((s) => s.id !== 'gf-stay-put');
    const recommended = buildRecommendedOptimalStrategies(arrangements, stayPut, { readings: candidateReadings });
    return { strategies, recommended, reasonableValue, stayPut, rerun: { candidates, legacyYears, date: futureEntryDate } };
}
